import { useSyncExternalStore } from "react";

/**
 * The list the page in front of the visitor shows, for the analytics: the `id` (UUID) and never the
 * slug, which carries the couple's names (D-02), plus the states the INST asks every list event to
 * carry (§10: `event_type`, `lifecycle_state`, `purchases_state`) when the page knows them.
 *
 * A list page publishes it (`ListContextPublisher`) and clears it when it leaves, so an id never
 * outlives the route that showed it — the `page_view` of the next route cannot read a stale list.
 * An external store because the publisher and the readers sit in different branches of the tree —
 * the page and the root layout — with no common parent to hold it.
 */

export type AnalyticsList = {
  list_id: string;
  event_type?: string;
  lifecycle_state?: string;
  purchases_state?: string;
};

let current: AnalyticsList | null = null;
const listeners = new Set<() => void>();

function sameList(a: AnalyticsList | null, b: AnalyticsList | null): boolean {
  if (a === null || b === null) return a === b;

  return (
    a.list_id === b.list_id &&
    a.event_type === b.event_type &&
    a.lifecycle_state === b.lifecycle_state &&
    a.purchases_state === b.purchases_state
  );
}

export function setAnalyticsList(next: AnalyticsList | null): void {
  if (sameList(current, next)) return;

  current = next;
  for (const listener of [...listeners]) listener();
}

export function getAnalyticsList(): AnalyticsList | null {
  return current;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);

  return () => {
    listeners.delete(onChange);
  };
}

/** The list on screen, or `null` on a page that shows none. `null` on the server. */
export function useAnalyticsList(): AnalyticsList | null {
  return useSyncExternalStore(subscribe, getAnalyticsList, () => null);
}

/** The id of the list on screen. A primitive, so a consumer re-renders only when the list changes. */
export function useAnalyticsListId(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => current?.list_id ?? null,
    () => null,
  );
}

/**
 * The list once a page has published it. With `timeoutMs`, `null` after that long if none does;
 * **without it, no limit**: a page that is slow to arrive (a slow read of the list) still gets its
 * `page_view` with the list, and the caller decides what to do when the person leaves first
 * (`PageViewTracker` sends it then, without the list). A `page_view` for a list route waits here so
 * that it leaves **with** the list, and never without it and then again.
 */
export function whenAnalyticsList(timeoutMs?: number): Promise<AnalyticsList | null> {
  if (current !== null) return Promise.resolve(current);

  return new Promise((resolve) => {
    const finish = (value: AnalyticsList | null) => {
      if (timer !== undefined) clearTimeout(timer);
      listeners.delete(onChange);
      resolve(value);
    };
    const onChange = () => {
      if (current !== null) finish(current);
    };
    const timer = timeoutMs === undefined ? undefined : setTimeout(() => finish(null), timeoutMs);

    listeners.add(onChange);
  });
}
